import { Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Tooltip, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import DynamicTable from '../../../Dynamic-table';

export interface LinkedLabReport {
    sampleCollectionId: string;
    trNo: number | string;
    sampleDate: string;
    collectionCenterName?: string;
    tests: { _id: string; testName: string; approvalStatus?: string }[];
    linkedAt: string;
    linkedBy?: string;
}

interface Props {
    open: boolean;
    onClose: () => void;
    mrn?: number | string;
    linkedReports: LinkedLabReport[];
    onLink: (sample: any) => void;
    onUnlink: (sampleCollectionId: string) => void;
}

const LOOKBACK_MONTHS = 12;
const MAX_SAMPLES = 20;

export const fetchPatientSamples = async (params: Record<string, any>) => {
    const response = await axios.get(
        `${process.env.REACT_APP_LAB_URL}/patient-result/findAllPatientSampleCollection`,
        {
            params: { count: MAX_SAMPLES, pageNo: 1, ...params },
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        }
    );
    return response?.data?.isSuccess ? response?.data?.data || [] : [];
};

export const approvalSummary = (tests: any[] = []) => {
    const approved = tests.filter((t: any) => t?.approvalStatus === 'Approved').length;
    return `${approved}/${tests.length} Approved`;
};

const LinkLabReportsDialog: React.FC<Props> = ({ open, onClose, mrn, linkedReports, onLink, onUnlink }) => {
    const [samples, setSamples] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open) return;
        if (!mrn) {
            toast.error('Patient MRN not found.');
            return;
        }
        const load = async () => {
            setLoading(true);
            try {
                setSamples(
                    await fetchPatientSamples({
                        fromDate: dayjs().subtract(LOOKBACK_MONTHS, 'month').format('YYYY-MM-DD'),
                        toDate: dayjs().format('YYYY-MM-DD'),
                        searchBy: 'MRN',
                        search: String(mrn),
                    })
                );
            } catch (error: any) {
                toast.error(error?.response?.data?.message || 'Failed to load lab samples.');
                setSamples([]);
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [open, mrn]);

    const isLinked = (id: string) => linkedReports.some(r => r.sampleCollectionId === id);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle sx={{ fontSize: '16px', fontWeight: 600 }}>
                Link Lab Reports
                <Typography sx={{ fontSize: '12px', color: 'gray' }}>
                    Latest sample collections of MRN {mrn} (last {LOOKBACK_MONTHS} months)
                </Typography>
            </DialogTitle>
            <DialogContent>
                <DynamicTable
                    height="55vh"
                    loading={loading}
                    headers={['TR No', 'Sample Date', 'Tests', 'Collection Center', 'Status', 'Action']}
                    data={samples.map((sample: any) => [
                        sample?.trNo,
                        dayjs(sample?.createdAt).format('DD.MM.YYYY hh:mm A'),
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {(sample?.testResults || []).map((test: any) => (
                                <Chip
                                    key={test?._id}
                                    size="small"
                                    label={test?.testName}
                                    color={test?.approvalStatus === 'Approved' ? 'success' : 'default'}
                                    variant="outlined"
                                    sx={{ fontSize: '10px' }}
                                />
                            ))}
                        </Box>,
                        sample?.collectionCenter?.collectionCenterName || '',
                        approvalSummary(sample?.testResults),
                    ])}
                    actionButtons={[
                        {
                            icon: (rowIndex: number) =>
                                isLinked(samples[rowIndex]?._id) ? (
                                    <Tooltip title="Unlink">
                                        <LinkOffIcon sx={{ color: '#FF5454' }} />
                                    </Tooltip>
                                ) : (
                                    <Tooltip title="Link">
                                        <LinkIcon sx={{ color: '#1976D2' }} />
                                    </Tooltip>
                                ),
                            onClick: (rowIndex: number) => {
                                const sample = samples[rowIndex];
                                if (isLinked(sample?._id)) {
                                    onUnlink(sample._id);
                                } else {
                                    onLink(sample);
                                }
                            },
                        },
                    ]}
                />
                {!loading && samples.length === 0 && (
                    <Typography sx={{ fontSize: '12px', textAlign: 'center', mt: 2, color: 'gray' }}>
                        No sample collections found for this patient.
                    </Typography>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} variant="contained">Done</Button>
            </DialogActions>
        </Dialog>
    );
};

export default LinkLabReportsDialog;
